import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/Button'
import { Select } from '@/components/ui/Select'
import { TextField } from '@/components/ui/TextField'
import { useI18n } from '../../i18n'
import { API_ENDPOINTS } from '../../config/api'
import { safeJson } from '../../lib/utils'
import {
  Building,
  Upload,
  FileText,
  CheckCircle,
  AlertCircle,
  Save,
  ChevronDown,
  ChevronUp,
  Shield,
} from 'lucide-react'
import styles from '../../styles/pages/SupplierPortalView.module.css'

type Supplier = {
  id: string
  name: string
  contactName?: string
  email?: string
  website?: string
  standards: string[]
  compliancePercent: number
  lastAssessment?: string
}

type Standard = {
  code: string
  name: string
  type: 'supplier' | 'regular'
  assessmentStatus?: string
  complianceScore?: number
  categories?: Array<{
    id: string
    name: string
    questions: Array<{
      id: string
      text_en: string
      text_ru: string
      text_es: string
      type?: string
      scaleMin?: number
      scaleMax?: number
    }>
  }>
}

type Assessment = {
  id: string
  standardCode: string
  status: string
  complianceScore: number
  updatedAt?: string
  evidence: Array<{
    id: string
    fileName?: string
    fileType?: string
    uploadedAt: string
  }>
}

const SupplierPortalView: React.FC = () => {
  const { token } = useParams<{ token: string }>()
  const navigate = useNavigate()
  const { t } = useI18n()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [supplier, setSupplier] = useState<Supplier | null>(null)
  const [standards, setStandards] = useState<Standard[]>([])
  const [assessments, setAssessments] = useState<Assessment[]>([])
  const [activeTab, setActiveTab] = useState<'profile' | 'certifications' | 'questionnaires' | 'securityPosture' | 'documents'>('profile')
  const [securityPostureAnswers, setSecurityPostureAnswers] = useState<Record<string, boolean | null>>({
    encryptionUsed: null,
    mfaUsed: null,
    ssdlcProcess: null,
    sastDast: null,
    infrastructureScanners: null,
    usesCloud: null
  })
  const [profileData, setProfileData] = useState({
    contactName: '',
    email: '',
    website: ''
  })
  const [answers, setAnswers] = useState<Record<string, Record<string, any>>>({})
  const [uploading, setUploading] = useState(false)
  const [questionnaireFilters, setQuestionnaireFilters] = useState({
    standard: 'all',
    category: 'all'
  })
  const [expandedCategories, setExpandedCategories] = useState<Record<string, Set<string>>>({})

  useEffect(() => {
    if (token) {
      fetchPortalData()
    }
  }, [token])

  const fetchPortalData = async () => {
    setLoading(true)
    try {
      const res = await fetch(`${API_ENDPOINTS.TPSM_PORTAL_TOKEN(token!)}`)
      const data = await safeJson(res)
      
      if (res.status === 403 || res.status === 404) {
        setError(data.error || 'Invalid or expired token')
        return
      }

      setSupplier(data.supplier)
      setStandards(data.standards || [])
      setAssessments(data.assessments || [])
      setProfileData({
        contactName: data.supplier.contactName || '',
        email: data.supplier.email || '',
        website: data.supplier.website || ''
      })
      
      // Load saved answers from standards data
      const savedAnswers: Record<string, Record<string, any>> = {}
      if (data.standards && Array.isArray(data.standards)) {
        data.standards.forEach((standard: any) => {
          if (standard.savedAnswers && typeof standard.savedAnswers === 'object') {
            savedAnswers[standard.code] = standard.savedAnswers
          }
        })
      }
      setAnswers(savedAnswers)
      
      // Load saved Security Posture answers from assessments
      const securityPostureData: Record<string, boolean | null> = {
        encryptionUsed: null,
        mfaUsed: null,
        ssdlcProcess: null,
        sastDast: null,
        infrastructureScanners: null,
        usesCloud: null
      }
      
      // Try to find Security Posture data in any assessment's findings.metadata
      if (data.assessments && Array.isArray(data.assessments)) {
        for (const assessment of data.assessments) {
          if (assessment.findings && typeof assessment.findings === 'object') {
            const metadata = assessment.findings.metadata || {}
            const securityPosture = metadata.securityPosture || {}
            
            if (securityPosture.encryptionUsed !== undefined) securityPostureData.encryptionUsed = securityPosture.encryptionUsed
            if (securityPosture.mfaUsed !== undefined) securityPostureData.mfaUsed = securityPosture.mfaUsed
            if (securityPosture.ssdlcProcess !== undefined) securityPostureData.ssdlcProcess = securityPosture.ssdlcProcess
            if (securityPosture.sastDast !== undefined) securityPostureData.sastDast = securityPosture.sastDast
            if (securityPosture.infrastructureScanners !== undefined) securityPostureData.infrastructureScanners = securityPosture.infrastructureScanners
            if (securityPosture.usesCloud !== undefined) securityPostureData.usesCloud = securityPosture.usesCloud
          }
        }
      }
      setSecurityPostureAnswers(securityPostureData)
      
    } catch (e: any) {
      setError(e.message || 'Failed to load portal data')
    } finally {
      setLoading(false)
    }
  }

  const handleProfileUpdate = async () => {
    try {
      const res = await fetch(`${API_ENDPOINTS.TPSM_PORTAL_TOKEN(token!)}/profile`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(profileData)
      })
      const data = await safeJson(res)
      
      if (res.ok) {
        setSupplier({ ...supplier!, ...data.supplier })
        alert(t('tpsm.portal.profileUpdated', 'Profile updated successfully'))
      } else {
        alert(data.error || 'Failed to update profile')
      }
    } catch (e: any) {
      alert('Failed to update profile')
    }
  }

  const handleFileUpload = async (file: File, type: 'certification' | 'document', standardCode?: string) => {
    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', file)
      if (standardCode) formData.append('standardCode', standardCode)

      const endpoint = type === 'certification' 
        ? `${API_ENDPOINTS.TPSM_PORTAL_TOKEN(token!)}/upload-certification`
        : `${API_ENDPOINTS.TPSM_PORTAL_TOKEN(token!)}/upload-document`

      const res = await fetch(endpoint, {
        method: 'POST',
        body: formData
      })
      const data = await safeJson(res)

      if (res.ok) {
        await fetchPortalData()
        alert(t('tpsm.portal.fileUploaded', 'File uploaded successfully'))
      } else {
        alert(data.error || 'Failed to upload file')
      }
    } catch (e: any) {
      alert('Failed to upload file')
    } finally {
      setUploading(false)
    }
  }

  const handleAnswerChange = (standardCode: string, questionId: string, value: any) => {
    setAnswers(prev => ({
      ...prev,
      [standardCode]: {
        ...prev[standardCode],
        [questionId]: value
      }
    }))
  }

  const handleSubmitSecurityPosture = async () => {
    try {
      // Check if at least one question is answered
      const hasAnswers = Object.values(securityPostureAnswers).some(answer => answer !== null)
      if (!hasAnswers) {
        alert(t('tpsm.portal.securityPosture.noAnswers', 'Please answer at least one question'))
        return
      }

      // Create or update a special assessment for Security Posture
      // We'll use a special standard code or store in metadata
      const res = await fetch(`${API_ENDPOINTS.TPSM_PORTAL_TOKEN(token!)}/submit-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          standardCode: 'SECURITY_POSTURE', // Special code for Security Posture
          answers: {}, // Empty answers for questions
          complianceScore: 0, // Will be calculated on backend
          securityPosture: securityPostureAnswers // Pass Security Posture data
        })
      })
      const data = await safeJson(res)

      if (res.ok) {
        alert(t('tpsm.portal.securityPosture.saved', 'Security Posture responses saved successfully'))
        await fetchPortalData()
      } else {
        alert(data.error || 'Failed to save Security Posture responses')
      }
    } catch (e: any) {
      console.error('Failed to save Security Posture:', e)
      alert('Failed to save Security Posture: ' + (e.message || 'Unknown error'))
    }
  }

  const handleSubmitAssessment = async (standardCode: string) => {
    try {
      const standardAnswers = answers[standardCode] || {}
      const answerCount = Object.keys(standardAnswers).length
      
      if (answerCount === 0) {
        alert(t('tpsm.portal.noAnswers', 'Please answer at least one question'))
        return
      }

      // Calculate compliance score based on answers
      // Backend will calculate properly, but send a basic score for now
      const answeredQuestions = Object.keys(standardAnswers).length
      const standard = standards.find(s => s.code === standardCode)
      const totalQuestions = standard?.categories?.reduce((sum, cat) => sum + (cat.questions?.length || 0), 0) || answeredQuestions
      const complianceScore = totalQuestions > 0 
        ? Math.min(100, Math.round((answeredQuestions / totalQuestions) * 100))
        : Math.min(100, Math.round((answeredQuestions / 10) * 100))

      const res = await fetch(`${API_ENDPOINTS.TPSM_PORTAL_TOKEN(token!)}/submit-assessment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          standardCode,
          answers: standardAnswers,
          complianceScore
        })
      })
      const data = await safeJson(res)

      if (res.ok) {
        alert(t('tpsm.portal.assessmentSubmitted', 'Assessment submitted successfully'))
        await fetchPortalData()
        setAnswers(prev => {
          const newAnswers = { ...prev }
          delete newAnswers[standardCode]
          return newAnswers
        })
      } else {
        alert(data.error || 'Failed to submit assessment')
      }
    } catch (e: any) {
      console.error('Failed to submit assessment:', e)
      alert('Failed to submit assessment: ' + (e.message || 'Unknown error'))
    }
  }

  if (loading) {
    return (
      <div className={styles.loadingCenter}>
        <p className={styles.loadingText}>{t('common.loading', 'Loading...')}</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.loadingCenter}>
        <div className={styles.errorCard}>
          <AlertCircle className={styles.errorIcon} aria-hidden />
          <h2 className={styles.errorTitle}>{t('tpsm.portal.invalidToken', 'Invalid Token')}</h2>
          <p className={styles.errorMsg}>{error}</p>
          <Button type="button" color="primary" onClick={() => navigate('/login')}>
            {t('tpsm.portal.goToLogin', 'Go to Login')}
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={`${styles.page} supplier-portal-view`}>
      <div className={styles.shell}>
        <div className={styles.brandBar}>
          <Shield className="h-8 w-8 shrink-0 opacity-95" aria-hidden />
          <h1 className={styles.brandTitle}>DefendSphere</h1>
        </div>

        <div className={styles.hero}>
          <Building className={styles.heroIcon} aria-hidden />
          <h2 className={styles.pageTitle}>{t('tpsm.portal.title', 'Supplier Portal')}</h2>
        </div>
        <p className={styles.subtitle}>
          {supplier?.name} — {t('tpsm.portal.subtitle', 'Complete your security assessment')}
        </p>

        <nav className={styles.tabs} aria-label="Portal sections">
          {(
            [
              { id: 'profile' as const, label: t('tpsm.portal.tabs.profile', 'Company Profile'), icon: Building },
              { id: 'certifications' as const, label: t('tpsm.portal.tabs.certifications', 'Certifications'), icon: FileText },
              { id: 'questionnaires' as const, label: t('tpsm.portal.tabs.questionnaires', 'Questionnaires'), icon: CheckCircle },
              { id: 'securityPosture' as const, label: t('tpsm.portal.tabs.securityPosture', 'Security Posture'), icon: Shield },
              { id: 'documents' as const, label: t('tpsm.portal.tabs.documents', 'Document Center'), icon: Upload },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <tab.icon aria-hidden />
              {tab.label}
            </button>
          ))}
        </nav>

        {/* Content */}
        {activeTab === 'profile' && (
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <h3 className={styles.panelTitle}>{t('tpsm.portal.profile.title', 'Company Profile')}</h3>
              <Button type="button" color="primary" size="small" onClick={handleProfileUpdate} icon={<Save className="h-4 w-4" aria-hidden />}>
                {t('tpsm.portal.profile.save', 'Save Changes')}
              </Button>
            </div>
            <div className={styles.panelBody}>
              <div className={styles.fieldStack}>
                <TextField
                  label={t('tpsm.portal.profile.companyName', 'Company Name')}
                  value={supplier?.name || ''}
                  onChange={() => {}}
                  disabled
                />
                <TextField
                  label={t('tpsm.portal.profile.contactName', 'Contact Name')}
                  value={profileData.contactName}
                  onChange={(v) => setProfileData({ ...profileData, contactName: v })}
                />
                <TextField
                  label={t('tpsm.portal.profile.email', 'Email')}
                  type="email"
                  value={profileData.email}
                  onChange={(v) => setProfileData({ ...profileData, email: v })}
                  autoComplete="email"
                />
                <TextField
                  label={t('tpsm.portal.profile.website', 'Website')}
                  type="url"
                  value={profileData.website}
                  onChange={(v) => setProfileData({ ...profileData, website: v })}
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'certifications' && (
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <h3 className={styles.panelTitle}>{t('tpsm.portal.certifications.title', 'Upload Certifications')}</h3>
            </div>
            <div className={styles.panelBody}>
              <div className={styles.stack}>
                {standards.map((std) => (
                  <div key={std.code} className={styles.subpanel}>
                    <h4 className={styles.subpanelTitle}>
                      {std.name} ({std.code})
                    </h4>
                    <div className="mb-3">
                      <label className="block text-xs font-semibold uppercase tracking-wide text-[var(--ds-gray-500)] mb-2">
                        {t('tpsm.portal.certifications.uploadFile', 'Upload Certificate File')}
                      </label>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.png,.jpg,.jpeg"
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            handleFileUpload(file, 'certification', std.code)
                          }
                        }}
                        className="block w-full text-sm file:mr-3 file:rounded-lg file:border-0 file:bg-[rgba(86,163,217,0.15)] file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-[var(--ds-primaryDark)]"
                        disabled={uploading}
                      />
                    </div>
                    {assessments
                      .filter((a) => a.standardCode === std.code)
                      .flatMap((a) => a.evidence)
                      .map((ev) => (
                        <div key={ev.id} className={styles.evidenceRow}>
                          <div className="flex items-center gap-2 min-w-0">
                            <FileText className="h-4 w-4 shrink-0 text-[var(--ds-primary)]" aria-hidden />
                            <span className="truncate text-sm">{ev.fileName}</span>
                          </div>
                          <span className={styles.metaMuted}>{new Date(ev.uploadedAt).toLocaleDateString()}</span>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'questionnaires' && (
          <div className={styles.stack}>
            <div className={styles.panel}>
              <div className={styles.panelHead}>
                <div>
                  <h3 className={styles.panelTitle}>{t('tpsm.portal.questionnaires.title', 'Questionnaires')}</h3>
                  <p className={`${styles.panelIntro} mb-0 mt-1`}>
                    {t('tpsm.portal.questionnaires.subtitle', 'Expand a category to answer questions inline. Click "Save responses" to persist your answers.')}
                  </p>
                </div>
              </div>
              <div className={styles.panelBody}>
                <div className={styles.filterRow}>
                  <Select<string>
                    label={t('tpsm.portal.questionnaires.filterStandard', 'Standard')}
                    className="w-full"
                    minWidth="100%"
                    options={[
                      { value: 'all', label: t('tpsm.portal.questionnaires.allStandards', 'All Standards') },
                      ...standards.map((std) => ({ value: std.code, label: std.name })),
                    ]}
                    value={questionnaireFilters.standard}
                    onChange={(v) => v != null && setQuestionnaireFilters({ ...questionnaireFilters, standard: v })}
                  />
                  <Select<string>
                    label={t('tpsm.portal.questionnaires.filterCategory', 'Category')}
                    className="w-full"
                    minWidth="100%"
                    options={[
                      { value: 'all', label: t('tpsm.portal.questionnaires.allCategories', 'All Categories') },
                      ...(() => {
                        const allCategories = new Set<string>()
                        standards.forEach((std) => {
                          std.categories?.forEach((cat) => {
                            if (cat.name) allCategories.add(cat.name)
                          })
                        })
                        return Array.from(allCategories)
                          .sort()
                          .map((cat) => ({ value: cat, label: cat }))
                      })(),
                    ]}
                    value={questionnaireFilters.category}
                    onChange={(v) => v != null && setQuestionnaireFilters({ ...questionnaireFilters, category: v })}
                  />
                </div>
              </div>
            </div>

            {standards.length === 0 ? (
              <div className={styles.panel}>
                <p className={styles.emptyState}>{t('tpsm.portal.noStandards', 'No standards assigned to this supplier')}</p>
              </div>
            ) : standards
              .filter(std => questionnaireFilters.standard === 'all' || std.code === questionnaireFilters.standard)
              .map(std => {
              const hasQuestions = std.categories && std.categories.length > 0 && std.categories.some(cat => cat.questions && cat.questions.length > 0)
              const assessment = assessments.find(a => a.standardCode === std.code)
              const assessmentStatus = std.assessmentStatus || assessment?.status || 'PENDING'
              const complianceScore = std.complianceScore !== undefined ? std.complianceScore : (assessment?.complianceScore || 0)
              const lastUpdated = assessment?.updatedAt ? new Date(assessment.updatedAt).toLocaleString() : null
              
              const statusBadgeClass =
                assessmentStatus === 'IN_PROGRESS'
                  ? styles.statusProgress
                  : assessmentStatus === 'COMPLETED'
                    ? styles.statusDone
                    : assessmentStatus === 'REVIEWED'
                      ? styles.statusReview
                      : styles.statusPending
              const statusLabel =
                assessmentStatus === 'IN_PROGRESS'
                  ? t('tpsm.portal.status.inProgress', 'In Progress')
                  : assessmentStatus === 'COMPLETED'
                    ? t('tpsm.portal.status.completed', 'Completed')
                    : assessmentStatus === 'REVIEWED'
                      ? t('tpsm.portal.status.reviewed', 'Reviewed')
                      : t('tpsm.portal.status.pending', 'Not Started')

              return (
              <div key={std.code} className={styles.panel}>
                <div className={styles.panelHead}>
                  <div className="w-full">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <h3 className={styles.panelTitle}>
                        {std.name} ({std.code})
                      </h3>
                      <span className={statusBadgeClass}>{statusLabel}</span>
                    </div>
                    <div className={`${styles.metaRow} mt-1`}>
                    {complianceScore > 0 && (
                      <span>
                        {t('tpsm.portal.complianceScore', 'Compliance Score')}:{' '}
                        <span className="font-semibold text-[var(--ds-primaryDark)]">{complianceScore}%</span>
                      </span>
                    )}
                    {lastUpdated && (
                      <span>
                        {t('tpsm.portal.lastUpdated', 'Last Updated')}: {lastUpdated}
                      </span>
                    )}
                    {answers[std.code] && Object.keys(answers[std.code]).length > 0 && (
                      <span className="text-[var(--ds-green-700,#15803d)]">
                        {t('tpsm.portal.answeredQuestions', '{{count}} questions answered', { count: Object.keys(answers[std.code]).length })}
                      </span>
                    )}
                  </div>
                  </div>
                </div>
                <div className={styles.panelBody}>
                  {hasQuestions ? (
                    <div className={styles.stack}>
                      {(std.categories ?? [])
                        .filter(cat => {
                          if (!cat.questions || cat.questions.length === 0) return false
                          if (questionnaireFilters.category === 'all') return true
                          return cat.name === questionnaireFilters.category
                        })
                        .map(category => {
                          const isExpanded = expandedCategories[std.code]?.has(category.id) ?? true
                          
                          return (
                            <div key={category.id} className={styles.categoryBlock}>
                              <div 
                                className={styles.categoryHeader}
                                onClick={() => {
                                  setExpandedCategories(prev => {
                                    const newState = { ...prev }
                                    if (!newState[std.code]) newState[std.code] = new Set()
                                    const stdCategories = new Set(newState[std.code])
                                    if (isExpanded) {
                                      stdCategories.delete(category.id)
                                    } else {
                                      stdCategories.add(category.id)
                                    }
                                    newState[std.code] = stdCategories
                                    return newState
                                  })
                                }}
                              >
                                <h4 className={styles.categoryName}>{category.name}</h4>
                                {isExpanded ? (
                                  <ChevronUp className="h-4 w-4 text-[var(--ds-gray-500)] shrink-0" aria-hidden />
                                ) : (
                                  <ChevronDown className="h-4 w-4 text-[var(--ds-gray-500)] shrink-0" aria-hidden />
                                )}
                              </div>
                              {isExpanded && (
                              <div className={styles.stack}>
                              {category.questions.map(question => {
                                const questionText = question.text_en || question.text_ru || question.text_es || 'Question'
                                const currentAnswer = answers[std.code]?.[question.id] || ''
                                
                                // Determine scale range (default 1-5 for compliance questions)
                                const scaleMin = question.scaleMin !== undefined && question.scaleMin !== null ? question.scaleMin : 1
                                const scaleMax = question.scaleMax !== undefined && question.scaleMax !== null ? question.scaleMax : 5
                                const hasScale = question.type === 'SCALE' || (question.type !== 'BOOLEAN' && question.type !== 'TEXT')

                                return (
                                  <div key={question.id} className={styles.questionBox}>
                                    <label className={styles.questionLabel}>
                                      {questionText}
                                    </label>
                                    {question.type === 'BOOLEAN' ? (
                                      <Select
                                        className="w-full max-w-md"
                                        minWidth="12rem"
                                        options={[
                                          { value: '', label: t('tpsm.portal.select', 'Select...') },
                                          { value: 'yes', label: t('tpsm.portal.yes', 'Yes') },
                                          { value: 'no', label: t('tpsm.portal.no', 'No') },
                                        ]}
                                        value={typeof currentAnswer === 'string' ? currentAnswer : String(currentAnswer ?? '')}
                                        onChange={(v) => handleAnswerChange(std.code, question.id, v ?? '')}
                                        placeholder={t('tpsm.portal.select', 'Select...')}
                                      />
                                    ) : hasScale ? (
                                      <div className="space-y-3">
                                        <div className="flex items-center gap-2 flex-wrap">
                                          <span className={`${styles.rangeHint} mr-1`}>{t('tpsm.portal.rating', 'Rating')}:</span>
                                          {[1, 2, 3, 4, 5].map((value) => (
                                            <label key={value} className="flex items-center gap-1 cursor-pointer">
                                              <input
                                                type="radio"
                                                name={`${std.code}_${question.id}`}
                                                value={value}
                                                checked={currentAnswer === value || currentAnswer === String(value)}
                                                onChange={(e) => handleAnswerChange(std.code, question.id, parseInt(e.target.value))}
                                                className="accent-[var(--ds-primary)]"
                                              />
                                              <span className={styles.ratingPill}>{value}</span>
                                            </label>
                                          ))}
                                        </div>
                                        <div className={styles.rangeHint}>
                                          {currentAnswer ? (
                                            <span className="font-medium text-[var(--ds-text)]">
                                              {currentAnswer === 1 || currentAnswer === '1' ? t('tpsm.portal.notImplemented', 'Not implemented') :
                                               currentAnswer === 2 || currentAnswer === '2' ? t('tpsm.portal.partiallyImplemented', 'Partially implemented') :
                                               currentAnswer === 3 || currentAnswer === '3' ? t('tpsm.portal.moderatelyImplemented', 'Moderately implemented') :
                                               currentAnswer === 4 || currentAnswer === '4' ? t('tpsm.portal.wellImplemented', 'Well implemented') :
                                               t('tpsm.portal.fullyImplemented', 'Fully implemented')}
                                            </span>
                                          ) : (
                                            <span>{t('tpsm.portal.notAnswered', 'Not answered')}</span>
                                          )}
                                        </div>
                                        <div className="mt-1">
                                          <input
                                            type="range"
                                            min={scaleMin}
                                            max={scaleMax}
                                            value={currentAnswer || scaleMin}
                                            onChange={(e) => handleAnswerChange(std.code, question.id, parseInt(e.target.value))}
                                            className="w-full accent-[var(--ds-primary)]"
                                          />
                                          <div className="flex justify-between mt-1 text-xs text-[var(--ds-gray-500)]">
                                            <span>{scaleMin}</span>
                                            <span className="font-medium">{currentAnswer || scaleMin}</span>
                                            <span>{scaleMax}</span>
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="space-y-2">
                                        <textarea
                                          value={currentAnswer}
                                          onChange={(e) => handleAnswerChange(std.code, question.id, e.target.value)}
                                          className={styles.textarea}
                                          rows={3}
                                          placeholder={t('tpsm.portal.enterAnswer', 'Enter your answer...')}
                                        />
                                        <div className={styles.optionalBlock}>
                                          <div className="flex items-center gap-2 flex-wrap">
                                            <span className={styles.rangeHint}>{t('tpsm.portal.optionalRating', 'Optional rating')}:</span>
                                            {[1, 2, 3, 4, 5].map((value) => (
                                              <label key={value} className="flex items-center gap-1 cursor-pointer">
                                                <input
                                                  type="radio"
                                                  name={`${std.code}_${question.id}_rating`}
                                                  value={value}
                                                  checked={answers[std.code]?.[`${question.id}_rating`] === value || answers[std.code]?.[`${question.id}_rating`] === String(value)}
                                                  onChange={(e) => handleAnswerChange(std.code, `${question.id}_rating`, parseInt(e.target.value))}
                                                  className="accent-[var(--ds-primary)]"
                                                />
                                                <span className={`${styles.rangeHint} font-medium`}>{value}</span>
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )
                              })}
                              </div>
                              )}
                            </div>
                          )
                        })}
                      <div className={styles.actionsBar}>
                        <Button
                          color="primary"
                          onClick={() => handleSubmitAssessment(std.code)}
                          disabled={!answers[std.code] || Object.keys(answers[std.code]).length === 0}
                        >
                          <CheckCircle />
                          {t('tpsm.portal.saveResponses', 'Save responses')}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.emptyState}>
                      {t('tpsm.portal.noQuestions', 'No questions available for this standard')}
                      {std.categories && std.categories.length > 0 && (
                        <div className="mt-2 text-xs opacity-90">
                          Found {std.categories.length} categor{std.categories.length === 1 ? 'y' : 'ies'}, but no questions loaded.
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )
            })}
          </div>
        )}

        {activeTab === 'securityPosture' && (
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <div className="flex flex-wrap items-start justify-between gap-2 w-full">
                <div>
                  <h3 className={styles.panelTitle}>{t('tpsm.portal.securityPosture.title', 'Security Posture Assessment')}</h3>
                  <p className={`${styles.panelIntro} mb-0 mt-1`}>
                    {t('tpsm.portal.securityPosture.subtitle', 'Answer these questions to help us assess your security posture. Your answers will affect your criticality score.')}
                  </p>
                </div>
                <Button color="primary" size="small" onClick={handleSubmitSecurityPosture}>
                  <Save />
                  {t('tpsm.portal.securityPosture.save', 'Save Responses')}
                </Button>
              </div>
            </div>
            <div className={styles.panelBody}>
              <div className={styles.stack}>
                <div className={styles.securityBlock}>
                  <h3>{t('tpsm.portal.securityPosture.encryptionUsed.question', 'A) Используется ли шифрование для взаимодействия с Организацией?')}</h3>
                  <div className={styles.radioRow}>
                    <label>
                      <input
                        type="radio"
                        name="encryptionUsed"
                        checked={securityPostureAnswers.encryptionUsed === true}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, encryptionUsed: true })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.yes', 'Yes')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="encryptionUsed"
                        checked={securityPostureAnswers.encryptionUsed === false}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, encryptionUsed: false })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.no', 'No')}</span>
                    </label>
                  </div>
                </div>

                <div className={styles.securityBlock}>
                  <h3>{t('tpsm.portal.securityPosture.mfaUsed.question', 'B) Применяет ли поставщик MFA?')}</h3>
                  <div className={styles.radioRow}>
                    <label>
                      <input
                        type="radio"
                        name="mfaUsed"
                        checked={securityPostureAnswers.mfaUsed === true}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, mfaUsed: true })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.yes', 'Yes')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="mfaUsed"
                        checked={securityPostureAnswers.mfaUsed === false}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, mfaUsed: false })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.no', 'No')}</span>
                    </label>
                  </div>
                </div>

                <div className={styles.securityBlock}>
                  <h3>{t('tpsm.portal.securityPosture.ssdlcProcess.question', 'C) Внедрен ли процесс безопасной разработки (SSDLC)?')}</h3>
                  <div className={styles.radioRow}>
                    <label>
                      <input
                        type="radio"
                        name="ssdlcProcess"
                        checked={securityPostureAnswers.ssdlcProcess === true}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, ssdlcProcess: true })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.yes', 'Yes')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="ssdlcProcess"
                        checked={securityPostureAnswers.ssdlcProcess === false}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, ssdlcProcess: false })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.no', 'No')}</span>
                    </label>
                  </div>
                </div>

                <div className={styles.securityBlock}>
                  <h3>{t('tpsm.portal.securityPosture.sastDast.question', 'D) Используются ли инструменты SAST/DAST?')}</h3>
                  <div className={styles.radioRow}>
                    <label>
                      <input
                        type="radio"
                        name="sastDast"
                        checked={securityPostureAnswers.sastDast === true}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, sastDast: true })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.yes', 'Yes')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="sastDast"
                        checked={securityPostureAnswers.sastDast === false}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, sastDast: false })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.no', 'No')}</span>
                    </label>
                  </div>
                </div>

                <div className={styles.securityBlock}>
                  <h3>{t('tpsm.portal.securityPosture.infrastructureScanners.question', 'E) Используются ли сканеры безопасности инфраструктуры?')}</h3>
                  <div className={styles.radioRow}>
                    <label>
                      <input
                        type="radio"
                        name="infrastructureScanners"
                        checked={securityPostureAnswers.infrastructureScanners === true}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, infrastructureScanners: true })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.yes', 'Yes')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="infrastructureScanners"
                        checked={securityPostureAnswers.infrastructureScanners === false}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, infrastructureScanners: false })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.no', 'No')}</span>
                    </label>
                  </div>
                </div>

                <div className={styles.securityBlock}>
                  <h3>{t('tpsm.portal.securityPosture.usesCloud.question', 'F) Использует ли поставщик облачные сервисы?')}</h3>
                  <div className={styles.radioRow}>
                    <label>
                      <input
                        type="radio"
                        name="usesCloud"
                        checked={securityPostureAnswers.usesCloud === true}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, usesCloud: true })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.yes', 'Yes')}</span>
                    </label>
                    <label>
                      <input
                        type="radio"
                        name="usesCloud"
                        checked={securityPostureAnswers.usesCloud === false}
                        onChange={() => setSecurityPostureAnswers({ ...securityPostureAnswers, usesCloud: false })}
                        className="accent-[var(--ds-primary)]"
                      />
                      <span>{t('common.no', 'No')}</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'documents' && (
          <div className={styles.panel}>
            <div className={styles.panelHead}>
              <h3 className={styles.panelTitle}>{t('tpsm.portal.documents.title', 'Document Center')}</h3>
            </div>
            <div className={styles.panelBody}>
              <div className={styles.stack}>
                <div className={styles.dropzone}>
                  <Upload className="h-10 w-10 mx-auto mb-3 text-[var(--ds-primary)] opacity-80" aria-hidden />
                  <input
                    id="supplier-portal-doc-file"
                    type="file"
                    className="sr-only"
                    onChange={(e) => {
                      const file = e.target.files?.[0]
                      if (file) {
                        handleFileUpload(file, 'document')
                      }
                    }}
                    disabled={uploading}
                  />
                  <Button
                    type="button"
                    color="secondary"
                    onClick={() => document.getElementById('supplier-portal-doc-file')?.click()}
                  >
                    {t('tpsm.portal.documents.selectFile', 'Select File to Upload')}
                  </Button>
                  <p className={`${styles.rangeHint} mt-2 mb-0 text-center`}>
                    {t('tpsm.portal.documents.allowedTypes', 'PDF, DOC, DOCX, XLS, XLSX, PNG, JPG, JPEG (max 10MB)')}
                  </p>
                </div>
                <div>
                  {assessments.flatMap((a) =>
                    a.evidence.map((ev) => (
                      <div key={ev.id} className={styles.docRow}>
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 shrink-0 text-[var(--ds-primary)]" aria-hidden />
                          <span className="truncate">{ev.fileName}</span>
                          <span className={styles.metaMuted}>({a.standardCode})</span>
                        </div>
                        <span className={styles.metaMuted}>{new Date(ev.uploadedAt).toLocaleDateString()}</span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default SupplierPortalView

